import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { trackEvent } from '../../utils/analytics';

const VIDEO_SRC =
  'https://pub-f55e661ab5a847d0b4d84f7e6685abc6.r2.dev/Vidya.ai%20-%20Intro%20Video.mp4';
const VIDEO_TITLE = 'product_demo';

const VideoPlayer: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const milestonesHit = useRef<Set<number>>(new Set());

  const getPercent = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration) return 0;
    return Math.round((v.currentTime / v.duration) * 100);
  }, []);

  const handlePlay = useCallback(() => {
    trackEvent('video_play', { video_title: VIDEO_TITLE });
  }, []);

  const handlePause = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Don't fire pause when video ends (ended fires separately)
    if (v.ended) return;
    trackEvent('video_pause', {
      video_title: VIDEO_TITLE,
      video_current_time: Math.round(v.currentTime),
      video_percent: getPercent(),
    });
  }, [getPercent]);

  const handleTimeUpdate = useCallback(() => {
    const percent = getPercent();
    const milestones = [25, 50, 75];
    for (const m of milestones) {
      if (percent >= m && !milestonesHit.current.has(m)) {
        milestonesHit.current.add(m);
        trackEvent('video_progress', {
          video_title: VIDEO_TITLE,
          video_percent: m,
          video_current_time: Math.round(videoRef.current?.currentTime ?? 0),
        });
      }
    }
  }, [getPercent]);

  const handleEnded = useCallback(() => {
    const v = videoRef.current;
    trackEvent('video_complete', {
      video_title: VIDEO_TITLE,
      video_duration: Math.round(v?.duration ?? 0),
    });
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto mb-16"
    >
      <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-black">
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          controls
          playsInline
          preload="metadata"
          onPlay={handlePlay}
          onPause={handlePause}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          className="w-full block"
        />
      </div>
    </motion.div>
  );
};

export default VideoPlayer;
